import { type FC } from 'react';

import SliceParameters, { getSliceDefaultParams } from './SliceParameters';
import InsidePolygonParameters, { getInsidePolygonDefaultParams } from './InsidePolygonParameters';
import ThresholdParameters, { getThresholdDefaultParams } from './ThresholdParameters';
import UnobstructedParameters, { getUnobstructedDefaultParams } from './UnobstructedParameters';
import ProjectionParameters, { getProjectionDefaultParams } from './ProjectionParameters';
import NoBaseParameters from './NoBaseParameters';
import AloneParameters from './AloneParameters';
import CloseToTerrainParameters, { getCloseToTerrainDefaultParams } from './CloseToTerrainParameters';
import CloseToProjectSketchParameters, { getCloseToProjectSketchDefaultParams } from './CloseToProjectSketchParameters';
import MapParameters from './MapParameters';
import RemoveSensorsByIdParameters, { getRemoveSensorsByIdDefaultParams } from './RemoveSensorsByIdParameters';
import ShadowParameters, { getShadowFilterDefaultParams } from './ShadowParameters';
import SolarParamsParameters, { getSolarParamsDefault } from './SolarParamsParameters';
import AveragesParameters, { getAveragesDefaultParams } from './AveragesParameters';
import { BlockKeys } from '@data/Simulations/pipeline/BlockCatalog';
import HideZeroValuesParameters from './HideZeroValuesParameters';
import PvPotentialsParameters, { getPvPotentialsDefaultParams } from './PvPotentialsParameters';
import ShgPotentialsParameters, { getShgPotentialsDefaultParams } from './ShgPotentialsParameters';

type PipelineParametersSectionProps = {
    blockId: string;
    blockType: string;
};

type ParameterComponent = FC<{ blockId: string }>;

type ParameterDefinition = {
    Component: ParameterComponent;
    hasParams: boolean;
    getDefaultParams?: () => Record<string, unknown>;
};

const PARAMETER_COMPONENTS: Record<string, ParameterDefinition | undefined> = {
    [BlockKeys.Slice]: {
        Component: SliceParameters,
        hasParams: true,
        getDefaultParams: () => getSliceDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.InsidePolygon]: {
        Component: InsidePolygonParameters,
        hasParams: true,
        getDefaultParams: () => getInsidePolygonDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.Threshold]: {
        Component: ThresholdParameters,
        hasParams: true,
        getDefaultParams: () => getThresholdDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.CloseToTerrain]: {
        Component: CloseToTerrainParameters,
        hasParams: true,
        getDefaultParams: () => getCloseToTerrainDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.CloseToProjSketch]: {
        Component: CloseToProjectSketchParameters,
        hasParams: true,
        getDefaultParams: () => getCloseToProjectSketchDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.Projection]: {
        Component: ProjectionParameters,
        hasParams: true,
        getDefaultParams: () => getProjectionDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.Unobstructed]: {
        Component: UnobstructedParameters,
        hasParams: true,
        getDefaultParams: () => getUnobstructedDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.Shadow]: {
        Component: ShadowParameters,
        hasParams: true,
        getDefaultParams: () => getShadowFilterDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.SolarParameters]: {
        Component: SolarParamsParameters,
        hasParams: true,
        getDefaultParams: () => getSolarParamsDefault() as Record<string, unknown>,
    },
    [BlockKeys.Alone]: { Component: AloneParameters, hasParams: false },
    [BlockKeys.NoBase]: { Component: NoBaseParameters, hasParams: false },
    [BlockKeys.Map]: { Component: MapParameters, hasParams: false },
    [BlockKeys.RemoveSensorsById]: {
        Component: RemoveSensorsByIdParameters,
        hasParams: false,
        getDefaultParams: () => getRemoveSensorsByIdDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.Averages]: {
        Component: AveragesParameters,
        hasParams: true,
        getDefaultParams: () => getAveragesDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.HideZeroValues]: {
        Component: HideZeroValuesParameters,
        hasParams: false,
    },
    [BlockKeys.PvPotentials]: {
        Component: PvPotentialsParameters,
        hasParams: true,
        getDefaultParams: () => getPvPotentialsDefaultParams() as Record<string, unknown>,
    },
    [BlockKeys.ShgPotentials]: {
        Component: ShgPotentialsParameters,
        hasParams: true,
        getDefaultParams: () => getShgPotentialsDefaultParams() as Record<string, unknown>,
    },
};

const PipelineParametersSection: FC<PipelineParametersSectionProps> & {
    hasParameters: (blockType: string) => boolean;
    getDefaultParams: (blockType: string) => Record<string, unknown> | undefined;
} = ({ blockId, blockType }) => {
    const definition = PARAMETER_COMPONENTS[blockType];
    if (!definition) {
        return null;
    }
    const { Component } = definition;

    return <Component key={`${blockType}-${blockId}`} blockId={blockId} />;
};

PipelineParametersSection.hasParameters = (blockType: string): boolean => {
    return Boolean(PARAMETER_COMPONENTS[blockType]?.hasParams);
};

PipelineParametersSection.getDefaultParams = (blockType: string) => {
    return PARAMETER_COMPONENTS[blockType]?.getDefaultParams?.();
};

export default PipelineParametersSection;
